import { useRef, useCallback } from 'react';

import { logger } from '@/services/logger/logger';
import { TransactionFetchRequest, TransactionFetchResponse, useLazyFetchTransactionQuery } from '@/api/payments/fetchTransaction';

export function useFetchTransaction() {
  const onSuccess = useRef<((data: TransactionFetchResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const [fetchTransactionTrigger, {
    isLoading,
    error,
  }] = useLazyFetchTransactionQuery();

  // Trigger payment-service fetch
  const triggerFetchApi = useCallback(async (
    transactionId: TransactionFetchRequest,
    callbacks?: {
      onSuccess?: (data: TransactionFetchResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    let data;

    try {
      data = await fetchTransactionTrigger(transactionId);
    } catch (err) {
      logger.error('Error fetching transaction data');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }

    // Handle payment-service fetch errors
    if (!!error || data?.data?.message !== 'success') {
      logger.error('Error fetching transaction data');
      logger.error(error);

      if (onError.current) {
        onError.current(error);
      }
      return null;
    }

    // Handle payment-service fetch complete
    if (onSuccess.current) {
      onSuccess.current(data.data);
    }

    return data.data.data;
  }, [error, fetchTransactionTrigger]);

  return {
    triggerFetchApi,
    fetchIsLoading: isLoading,
    fetchError: error,
  };
}
